'use client';

import { useTranslation } from 'next-i18next/pages';
import { FiChevronDown } from 'react-icons/fi';

import { Group, Menu, UnstyledButton } from '@highlight-cp/core-ui';

import { StatusColorDot } from '../status-color-dot';
import classes from './draft-ready-menu.module.css';

interface DraftReadyMenuProps {
  /**
   * Whether the thing being marked is ready.
   *
   * @default false
   */
  enabled?: boolean;
  /**
   * Whether the control is disabled — the menu stays shut and nothing can be picked.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * The function to call when a different state is picked. Picking the state already showing is a
   * no-op, so this only fires on an actual change.
   */
  onChange: (value: boolean) => void;
}

/**
 * The two states, in menu order. `ariaLabel` because "DRAFT" alone doesn't say what it's the status
 * *of*, and the visible label has to stay part of the accessible name — so it's the whole phrase
 * rather than an interpolation. The pill upper-cases `label` in CSS, which is why the shouty form
 * isn't a second string to translate. Deliberately not `PROGRESS_STATUS_LABEL`, which calls this
 * state "Drafting": the design says "Draft".
 */
const OPTIONS = [
  { status: 'draft', label: 'Draft', ariaLabel: 'Status: Draft' },
  { status: 'ready', label: 'Ready', ariaLabel: 'Status: Ready' },
] as const;

/**
 * ### DraftReadyMenu
 *
 * @description
 * The Draft/Ready control for anything that gets marked ready before fielding — surveys, screeners,
 * fielding groups, test groups, product groups, segments. A compact pill showing the current state
 * (amber drafting, green ready) that opens a menu of both states.
 *
 * Controlled: pass `enabled` and handle `onChange`. Whether a state *may* be picked is the caller's
 * business — this control always offers both.
 *
 * @example
 * ```tsx
 * <DraftReadyMenu enabled={isReady} onChange={setIsReady} />
 * ```
 *
 * @param props - {@link DraftReadyMenuProps}
 * @returns React component
 */
const DraftReadyMenu = ({ enabled = false, disabled = false, onChange }: DraftReadyMenuProps) => {
  const { t } = useTranslation();
  const current = OPTIONS[enabled ? 1 : 0];

  return (
    <Menu disabled={disabled} position="bottom-start" withinPortal>
      <Menu.Target>
        {/* The pill names the status itself, so its dot is decorative — otherwise the state is read
            out twice. */}
        <UnstyledButton
          aria-label={t(current.ariaLabel)}
          className={classes.pill}
          data-status={current.status}
          disabled={disabled}
        >
          <StatusColorDot decorative size={8} status={current.status} variant="leading" />
          {t(current.label)}
          <FiChevronDown size={16} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.RadioGroup value={current.status} onChange={(value) => onChange(value === 'ready')}>
          {OPTIONS.map((option) => (
            <Menu.RadioItem
              key={option.status}
              // The dots mark the options; Mantine's own radio indicator would sit beside them
              // saying the same thing twice (see the theme's menu.module.css).
              mod={{ hideIndicator: true }}
              value={option.status}
            >
              <Group gap="xs" wrap="nowrap">
                <StatusColorDot decorative size={8} status={option.status} variant="leading" />
                {t(option.label)}
              </Group>
            </Menu.RadioItem>
          ))}
        </Menu.RadioGroup>
      </Menu.Dropdown>
    </Menu>
  );
};

export default DraftReadyMenu;
